<template>
    <div>
        <b-modal
            :visible="value"
            :id="modal_id"
            size="lg"
            :no-close-on-backdrop="publication_modified"
            :no-close-on-esc="publication_modified"
            @cancel="cancel"
            @close="cancel"
            @hidden="hidden"
            @ok="savePublication"
        >
            <template #modal-title>
                <div class="d-flex w-100 align-items-center">

                    <span v-if="editable_publication.id">
                        Edit
                        <span v-if="is_trunk">Trunk</span>
                        <span v-else>Publication</span>
                    </span>
                    <span v-else>
                        Create
                        <span v-if="is_trunk">Trunk</span>
                        <span v-else>Publication</span>
                    </span>
                </div>
                <div v-if="editable_publication_cache.title" class="text-muted">
                    <small>{{ editable_publication_cache.title }}</small>
                </div>
            </template>
            <div>
                <b-alert variant="warning" :show="publication.locked">
                    This Publication is locked. Unlock it to make changes.
                </b-alert>
            </div>

            <div class="row">
                <div class="col-lg-4">
                    <b-form-group label="Subject" label-for="subject">
                        <b-form-select
                            ref="subject"
                            v-model="editable_publication.subject"
                            :class="{'is-invalid': validating && !editable_publication.subject}"
                            :disabled="publication.locked"
                        >
                            <b-form-select-option value="null" selected disabled>
                                Subject
                            </b-form-select-option>
                            <b-form-select-option
                                v-for="(subject, index) in subjects"
                                :key="index"
                                :value="subject.subject"
                            >
                                {{ subject.name }}
                            </b-form-select-option>
                        </b-form-select>

                        <b-form-invalid-feedback>
                            Subject is required
                        </b-form-invalid-feedback>
                    </b-form-group>
                </div>
                <div v-if="!is_trunk" class="col-lg-4">
                    <b-form-group label="School Year" label-for="school_year">
                        <b-form-select
                            ref="school_year"
                            v-model="editable_publication.school_year"
                            :class="{'is-invalid': validating && !editable_publication.school_year}"
                            :disabled="publication.locked"
                        >
                            <b-form-select-option value="null" selected disabled>
                                School Year
                            </b-form-select-option>
                            <b-form-select-option
                                v-for="(school_year, index) in school_years"
                                :key="index"
                                :value="school_year.school_year"
                            >
                                {{ school_year.school_year }}
                            </b-form-select-option>
                        </b-form-select>

                        <b-form-invalid-feedback>
                            School Year is required
                        </b-form-invalid-feedback>
                    </b-form-group>
                </div>
                <div v-if="!is_trunk" class="col-lg-4">
                    <b-form-group label="Copyright Year (3.0)" label-for="copyright_year">
                        <b-form-select
                            ref="copyright_year"
                            v-model="editable_publication.copyright_year"
                            :disabled="publication.locked"
                        >
                            <b-form-select-option value="null" selected disabled>
                                Copyright Year
                            </b-form-select-option>
                            <b-form-select-option
                                v-for="(school_year, index) in school_years"
                                :key="index"
                                :value="school_year.school_year"
                            >
                                {{ school_year.school_year }}
                            </b-form-select-option>
                        </b-form-select>
                    </b-form-group>
                </div>
            </div>

            <div v-if="!is_trunk" class="h6 mb-3">
                Grades And States - Assigned in
                <span v-if="editable_publication.state_grades">{{ editable_publication.state_grades.length }}</span>
                <span v-else>0</span>
                State(s)
                <span
                    class="collapse-icon mr-1"
                    :class="states_visible ? null : 'collapsed'"
                    :aria-expanded="states_visible ? 'true' : 'false'"
                    @click="toggleStatesVisible"
                >
                    <span class="open">
                        <font-awesome-icon :icon="['fas', 'caret-down']" fixed-width/>
                    </span>
                    <span class="closed">
                        <font-awesome-icon :icon="['fas', 'caret-right']" fixed-width/>
                    </span>

                </span>
            </div>
            <b-collapse
                v-model="states_visible"
                :class="states_visible ? null : 'collapsed'"
            >
                <div
                    v-if="!is_trunk"
                    v-for="(row, index) in editable_publication.state_grades" :key="index"
                    class="d-flex align-items-center mb-3"
                >
                    <div class="row flex-grow-1">
                        <div class="col-4">
                            <b-form-group label="State" label-for="state" class="mb-0">
                                <b-form-select
                                    :ref="'state' + index"
                                    v-model="row.state"
                                    :class="{'is-invalid': validating && !row.state}"
                                    :disabled="publication.locked"
                                    @change="reorderStateGrades()"
                                >
                                    <b-form-select-option value="null" selected disabled >
                                        State
                                    </b-form-select-option>
                                    <b-form-select-option
                                        v-for="(state, index) in states"
                                        :key="index"
                                        :value="state.abbreviation"
                                    >
                                        {{ state.name }}
                                    </b-form-select-option>
                                </b-form-select>

                                <b-form-invalid-feedback>
                                    State is required
                                </b-form-invalid-feedback>
                            </b-form-group>
                        </div>

                        <div
                            class="col-4 d-flex"
                        >
                            <div class="flex-grow-1">
                                <b-form-group label="Grade" label-for="grade" class="mb-0">
                                    <div class="d-flex align-items-center">
                                        <div class="flex-grow-1">
                                            <b-form-select
                                                :ref="'grade' + index" v-model="row.grade"
                                                :class="{'is-invalid': validating && !row.grade}"
                                                :disabled="publication.locked"
                                            >
                                                <b-form-select-option value="null" selected disabled>
                                                    Grade
                                                </b-form-select-option>
                                                <b-form-select-option
                                                    v-for="(grade, index) in grades"
                                                    :key="index"
                                                    :value="grade.grade"
                                                >
                                                    <span
                                                        v-if="grade.grade == 'p' || grade.grade < 1 || grade.grade == 1988"
                                                    >
                                                        {{ grade.name }}
                                                    </span>
                                                        <span v-else>
                                                        {{ grade.grade }}
                                                    </span>
                                                </b-form-select-option>
                                            </b-form-select>
                                        </div>
                                        
                                    </div>

                                    <b-form-invalid-feedback :class="{'d-block': validating && !row.grade}">
                                        Grade is required
                                    </b-form-invalid-feedback>
                                </b-form-group>
                            </div>
                        </div>
                        <div
                        class="col-4 d-flex"
                        :class="{'align-items-end': !validating, 'align-items-center': validating}"
                        >
                            <b-form-group label="Standards Type" label-for="standards_type">
                                <b-form-select
                                    ref="standards_type"
                                    v-model="row.standards_source"
                                    @change="updateOtherGrades($event, row.state, row.grade)"
                                >
                                    <b-form-select-option value="null" selected disabled>
                                        Standards Type
                                    </b-form-select-option>
                                    <b-form-select-option
                                        value="none"
                                        :disabled="checkStandardTypeAvailability(row.state, row.grade, 'none')"
                                    >
                                        None
                                    </b-form-select-option>
                                    <b-form-select-option
                                        value="state"
                                        :disabled="checkStandardTypeAvailability(row.state, row.grade, 'state')"
                                    >
                                        State
                                    </b-form-select-option>
                                    <b-form-select-option
                                        value="national"
                                        :disabled="checkStandardTypeAvailability(row.state, row.grade, 'national')"
                                    >
                                        National
                                    </b-form-select-option>
                                </b-form-select>

                                <b-form-invalid-feedback>
                                    Standards Year is required
                                </b-form-invalid-feedback>
                            </b-form-group>
                            <div
                                v-if="editable_publication.state_grades && editable_publication.state_grades.length > 1"
                                class="ml-3 mb-3"
                            >
                                <b-btn
                                    variant="danger"
                                    @click="removeStateGrade(index)"
                                    :disabled="publication.locked"
                                >
                                    <font-awesome-icon :icon="['far', 'minus']"/>
                                </b-btn>
                            </div>
                        </div>
                    </div>
                </div>

                <div
                    v-if="!is_trunk && !publication.locked"
                    class="mb-3"
                >
                <a href="#" @click="adding_state = !adding_state">
                    <small><font-awesome-icon :icon="['far', 'plus-square']"/> Add state</small>
                </a>
                <div v-if="adding_state" class="row">
                    <div class="col-4">
                        <b-form-group label="State" label-for="state" class="mb-0">
                            <b-form-select
                                v-model="state_to_add"
                            >
                                <b-form-select-option value="null" selected disabled >
                                    State
                                </b-form-select-option>
                                <b-form-select-option
                                    v-for="(state, index) in states"
                                    :key="index"
                                    :value="state.abbreviation"
                                >
                                    {{ state.name }}
                                </b-form-select-option>
                            </b-form-select>
                        </b-form-group>
                    </div>
                    <div
                        class="col-4 d-flex"
                    >
                        <div class="flex-grow-1">
                            <b-form-group label="Grade" label-for="grade" class="mb-0">
                                <div class="d-flex align-items-center">
                                    <div class="flex-grow-1">
                                        <b-form-select
                                        v-model="grade_to_add"
                                        >
                                            <b-form-select-option value="null" selected disabled>
                                                Grade
                                            </b-form-select-option>
                                            <b-form-select-option
                                                v-for="(grade, index) in grades"
                                                :key="index"
                                                :value="grade.grade"
                                            >
                                                <span
                                                    v-if="grade.grade == 'p' || grade.grade < 1 || grade.grade == 1988"
                                                >
                                                    {{ grade.name }}
                                                </span>
                                                    <span v-else>
                                                    {{ grade.grade }}
                                                </span>
                                            </b-form-select-option>
                                        </b-form-select>
                                    </div>
                                </div>
                            </b-form-group>
                        </div>
                    </div>
                    <div
                        class="col-4 d-flex"
                    >
                        <b-form-group label="Standards Type">
                            <b-form-select
                                v-model="standards_source_to_add"
                            >
                                <b-form-select-option value="null" selected disabled>
                                    Standards Type
                                </b-form-select-option>
                                <b-form-select-option
                                    value="state"
                                >
                                    State
                                </b-form-select-option>
                                <b-form-select-option
                                    value="national"
                                >
                                    National
                                </b-form-select-option>
                            </b-form-select>
                        </b-form-group>
                        <div
                            v-if="state_to_add && grade_to_add && standards_source_to_add && !is_duplicate_state_grade"
                            class="ml-3 mb-3"
                        >
                            <b-btn
                                variant="success"
                                @click="addNewStateGrade()"
                                :disabled="publication.locked"
                            >
                                <font-awesome-icon :icon="['far', 'plus']"/>
                            </b-btn>
                        </div>
                    </div>
                </div>
                <div class="alert alert-danger" v-if="is_duplicate_state_grade">This State and Grade combo is already added</div>
                </div>
                <div
                    v-if="!is_trunk && !publication.locked"
                    class="mb-3"
                >
                    <a href="#" @click.prevent="showDefaultGradeSelect">
                        <small><font-awesome-icon :icon="['far', 'plus-square']"/> Add all states</small>
                    </a>
                    <div class="flex-grow-1" v-if="show_default_grade_select">
                        <small>
                            Select default grade(this grade will be added for all states and can then be edited.)
                        </small>
                        <b-form-select v-model="default_grade_to_add">
                            <b-form-select-option value="null" selected disabled>
                                Grade
                            </b-form-select-option>
                            <b-form-select-option v-for="(grade, index) in grades" :key="index" :value="grade.grade">
                            <span v-if="grade.grade == 'p' || grade.grade < 1">
                                {{ grade.name }}
                            </span>
                                <span v-else>
                                {{ grade.grade }}
                            </span>
                            </b-form-select-option>
                        </b-form-select>
                        <a href="#" @click.prevent="addAllStates">
                            <small>Add States</small>
                        </a>
                    </div>
                </div>
            </b-collapse>

            <div class="row">
                <div v-if="!is_trunk" class="col-lg-6">
                    <b-form-group label="Default Grade" label-for="default_grade">
                        <b-form-select
                            ref="format"
                            v-model="editable_publication.default_grade"
                            :disabled="publication.locked"
                        >
                            <b-form-select-option value="null" selected disabled>
                                Default Grade
                            </b-form-select-option>
                            <b-form-select-option v-for="(grade, index) in grades" :key="index" :value="grade.grade">
                                {{ grade.name }}
                            </b-form-select-option>
                        </b-form-select>
                    </b-form-group>
                </div>

                <div class="col-lg-6">
                    <b-form-group label="Format" label-for="format">
                        <b-form-select
                            ref="format"
                            v-model="editable_publication.format"
                            :class="{'is-invalid': validating && !editable_publication.format}"
                            :disabled="publication.locked"
                        >
                            <b-form-select-option value="null" selected disabled>
                                Format
                            </b-form-select-option>
                            <b-form-select-option v-for="(format, index) in formats" :key="index"
                                                  :value="format.format">
                                {{ format.title }}
                            </b-form-select-option>
                        </b-form-select>

                        <b-form-invalid-feedback>Format is required</b-form-invalid-feedback>
                    </b-form-group>
                </div>

                <div class="col-lg-6">
                    <b-form-group label="Language" label-for="language">
                        <language-multiselect
                            v-model="language"
                            :options="languages"
                            :class="{'is-invalid': validating && !editable_publication.language}"
                            @add="addLanguage"
                            @remove="removeLanguage"
                            :disabled="publication.locked"
                        />
                        <b-form-invalid-feedback :state="validating && !editable_publication.language ? false : null">
                            Language is required
                        </b-form-invalid-feedback>
                    </b-form-group>
                </div>

                <div class="col-lg-6">
                    <b-form-group label="SKU" label-for="sku">
                        <b-form-input
                            ref="sku"
                            type="text"
                            :class="{'is-valid': editable_publication.sku && sku_available, 'is-invalid': validating && !editable_publication.sku || sku_available == false}"
                            v-model.trim="editable_publication.sku"
                            @input="formatSku(editable_publication.sku)"
                            :disabled="publication.locked"
                        />

                        <b-form-valid-feedback>SKU is available</b-form-valid-feedback>
                        <b-form-invalid-feedback v-if="sku_available == false">
                            SKU is unavailable
                        </b-form-invalid-feedback>
                        <b-form-invalid-feedback v-if="validating && !editable_publication.sku">
                            SKU is required
                        </b-form-invalid-feedback>
                    </b-form-group>
                </div>

                <div v-if="!is_trunk" class="col-lg-6">
                    <b-form-group label="Sample SKU" label-for="sample_sku">
                        <b-input-group>
                            <b-form-input
                                ref="sample_sku"
                                type="text"
                                :class="{'is-valid': editable_publication.sample_sku && sample_sku_available, 'is-invalid': sample_sku_available == false}"
                                v-model.trim="editable_publication.sample_sku"
                                :readonly="!sample_sku_editable"
                                @input="formatSampleSku(editable_publication.sample_sku)"
                                :disabled="publication.locked"
                            />

                            <b-input-group-append>
                                <b-btn
                                    variant="light"
                                    :class="{'active': sample_sku_editable}"
                                    @click="sample_sku_editable = !sample_sku_editable"
                                    :disabled="publication.locked"
                                >
                                    <span v-if="sample_sku_editable">
                                        <font-awesome-icon :icon="['fad', 'unlock']" fixed-width/>
                                    </span>
                                    <span v-else>
                                        <font-awesome-icon :icon="['fad', 'lock']" fixed-width/>
                                    </span>
                                </b-btn>
                            </b-input-group-append>
                        </b-input-group>

                        <b-form-valid-feedback
                            :class="{'d-block': editable_publication.sample_sku && sample_sku_available == true}"
                            v-if="editable_publication.sample_sku && sample_sku_available == true"
                        >
                            Sample SKU is available
                        </b-form-valid-feedback>

                        <b-form-invalid-feedback
                            :class="{'d-block': editable_publication.sample_sku && sample_sku_available == false}"
                            v-if="editable_publication.sample_sku && sample_sku_available == false"
                        >
                            Sample SKU is unavailable
                        </b-form-invalid-feedback>
                    </b-form-group>
                </div>

                <div v-if="!is_trunk" class="col-lg-6">
                    <b-form-group label="Physical SKU (Only Kris Gillespie)" label-for="physical_sku">
                        <b-form-select
                            ref="physical_sku"
                            v-model="editable_publication.legacy_id"
                            :disabled="!can_set_portal_sku || publication.locked"
                        >
                            <b-form-select-option value="0" selected>
                                NO Physical SKU Selected
                            </b-form-select-option>
                            <b-form-select-option v-for="(leg_ids, index) in legacy_id_options" :key="index"
                                                  :value="leg_ids.ID">
                                {{ leg_ids.sku }}
                            </b-form-select-option>
                        </b-form-select>
                    </b-form-group>
                </div>

                <div v-if="!is_trunk" class="col-lg-6">
                    <b-form-group label="Availability" label-for="availability">
                        <b-form-select
                            ref="availability"
                            v-model="editable_publication.availability"
                            :disabled="publication.locked"
                        >
                            <b-form-select-option value="null" selected>
                                No Availability Selected
                            </b-form-select-option>
                            <b-form-select-option
                                v-for="(availability, index) in availabilities"
                                :key="index"
                                :value="availability.availability"
                            >
                                {{ availability.name }}
                            </b-form-select-option>
                        </b-form-select>
                    </b-form-group>
                </div>
                <div v-if="!is_trunk" class="col-lg-6">
                    <b-form-group label="Category" label-for="category">
                        <b-form-select
                            ref="category"
                            v-model="editable_publication.category"
                            :disabled="publication.locked"
                        >
                            <b-form-select-option value="null" selected>
                                No Category Selected
                            </b-form-select-option>
                            <b-form-select-option
                                v-for="(category, index) in categories"
                                :key="index"
                                :value="category.category"
                            >
                                {{ category.name }}
                            </b-form-select-option>
                        </b-form-select>
                    </b-form-group>
                </div>

                <div class="col-lg-12">
                    <b-form-group label="Title" label-for="title">
                        <b-input-group>
                            <b-form-textarea
                                ref="title"
                                type="text"
                                :class="{'is-invalid': validating && !editable_publication.title}"
                                v-model="editable_publication.title"
                                :readonly="!title_editable && !is_trunk"
                                :disabled="publication.locked"
                            />

                            <b-input-group-append>
                                <b-btn
                                    variant="light"
                                    :class="{'active': title_editable}"
                                    @click="title_editable = !title_editable"
                                    :disabled="publication.locked"
                                >
                                    <span v-if="title_editable">
                                        <font-awesome-icon :icon="['fad', 'unlock']" fixed-width/>
                                    </span>
                                    <span v-else>
                                        <font-awesome-icon :icon="['fad', 'lock']" fixed-width/>
                                    </span>
                                </b-btn>
                            </b-input-group-append>
                        </b-input-group>

                        <b-form-text id="title-suggestion" v-if="suggested_title && !publication.locked">
                            <span class="text-muted">Suggested Title: </span>
                            <b-btn
                                variant="link"
                                class="p-0 border-0"
                                size="sm"
                                @click.prevent="useSuggestedTitle(suggested_title)"
                            >
                                {{ suggested_title }}
                            </b-btn>
                        </b-form-text>

                        <b-form-invalid-feedback :class="{'d-block': validating && !editable_publication.title}">
                            Title is required
                        </b-form-invalid-feedback>
                    </b-form-group>
                </div>

                <div class="col-lg-12">
                    <b-form-group label="Internal Notes" label-for="internal_notes">
                        <TipTap
                            ref="internal_notes"
                            v-model="editable_publication.internal_notes"
                            :menu_items="['bold', 'italic', 'underline']"
                            :hide_word_count="true"
                            :exclude_table="true"
                            :exclude_save="true"
                            :exclude_copy="true"
                        />
                    </b-form-group>
                </div>

                <div v-if="!is_trunk" class="col-lg-6">
                    <b-form-group label="ISSN" label-for="issn" description="Optional">
                        <b-form-input type="text" v-model="editable_publication.issn" :disabled="publication.locked"/>
                    </b-form-group>
                </div>

                <div v-if="!is_trunk" class="col-lg-6">
                    <b-form-group label="ISBN TE" label-for="isbn" description="Optional">
                        <b-form-input type="text" v-model="editable_publication.isbn" :disabled="publication.locked"/>
                    </b-form-group>
                </div>

                <div v-if="!is_trunk" class="col-lg-6">
                    <b-form-group label="ISBN SE" label-for="isbn_se" description="Optional">
                        <b-form-input type="text" v-model="editable_publication.isbn_se" :disabled="publication.locked"/>
                    </b-form-group>
                </div>

                <div v-if="!is_trunk" class="col-lg-6">
                    <b-form-group label="ISBN Big Ed" label-for="isbn_big_ed" description="Optional">
                        <b-form-input type="text" v-model="editable_publication.isbn_big_ed" :disabled="publication.locked"/>
                    </b-form-group>
                </div>

                <div v-if="!is_trunk" class="col-lg-6">
                    <b-form-group label="Add Badge" label-for="badge_type" description="Optional">
                        <b-form-select
                            ref="badge_type"
                            v-model="editable_publication.badge_type"
                            :disabled="publication.locked"
                        >
                            <b-form-select-option :value="null" selected></b-form-select-option>
                            <b-form-select-option
                                v-for="(badge_type, index) in badge_types"
                                :key="index"
                                :value="badge_type.badge_type"
                            >
                                {{ badge_type.name }}
                            </b-form-select-option>
                        </b-form-select>
                    </b-form-group>
                </div>
                <div
                    v-if="editable_publication.badge_type && editable_publication.badge_type !== 'discontinued'"
                    class="col-lg-6"
                >
                    <b-form-group label="Badge End Date" label-for="badge_end_date">
                        <b-form-datepicker
                            ref="badge_end_date"
                            id="badge_end_date"
                            v-model="editable_publication.badge_end_date"
                            :class="{'is-invalid': validating && (editable_publication.badge_type && !editable_publication.badge_end_date)}"
                            :disabled="publication.locked"
                        />
                        <b-form-invalid-feedback
                            :class="{'d-block': validating && (editable_publication.badge_type && !editable_publication.badge_end_date)}"
                        >
                            Badge End Date is required
                        </b-form-invalid-feedback>
                    </b-form-group>
                </div>


                <div v-if="!is_trunk" class="col-lg-8">
                    <b-form-group label="Link publication" label-for="editable_publication.linked_publication">
                        <b-form-text>
                            To link a publication please contact Kris Gillespie
                        </b-form-text>
                        <multiselect
                            v-model="editable_publication.linked_publication"
                            :options="linkablePublications"
                            label="sku"
                            track-by="sku"
                            :disabled="!can_link_publication || publication.locked"
                        >
                            <template
                                v-for="slotName in ['option', 'singleLabel']"
                                #[slotName]="{ option }"
                            >
                                <div
                                    :key="slotName"
                                    :class="slotName === 'option' ? 'option__desc' : null"
                                >
                                    <span :class="slotName === 'option' ? 'option__title' : null">
                                        <span>{{ option.title || 'no title' }}</span>
                                        <span>({{ option.sku }})</span>
                                    </span>
                                </div>
                            </template>
                        </multiselect>
                        <b-form-text>
                            This will link all content from the selected publication to this publication.
                        </b-form-text>
                    </b-form-group>
                </div>

                <div class="col-lg-12 mt-3" v-if="!updating_intro_video">
                    <div>
                        <div class="row">
                            <div class="col-12 mt-3">
                                <div v-if="!is_trunk">
                                    <b-form-group
                                        label="Publication Intro Video Title"
                                        label-for="publication_intro_video_title"
                                        description=""
                                    >
                                        <b-form-input
                                            ref="intro_video_title"
                                            type="text"
                                            v-model="editable_publication.intro_video_title"
                                            :class="{'is-invalid': validating && isIntroVideoTitleMissing()}"
                                            :disabled="publication.locked"
                                        />
                                        <b-form-invalid-feedback :class="{'d-block': validating && isIntroVideoTitleMissing()}">
                                            Intro Video Title is required when Intro Video URL is set
                                        </b-form-invalid-feedback>
                                    </b-form-group>
                                </div>
                            </div>
                            <div class="col-6" v-if="!is_trunk">
                                Publication Intro Video Splash Image
                                <div style="border: solid lightgray 1px;">
                                    <img
                                        v-if="editable_publication.intro_video_splash_url"
                                        :src="editable_publication.intro_video_splash_url"
                                        class="img-fluid img-thumbnail"
                                    >
                                    <div v-else class="p-4"> None Selected</div>
                                </div>
                            </div>
                            <div class="col-6" v-if="!is_trunk">
                                <div>
                                    Publication Intro Video
                                    <div style="border: solid lightgray 1px;">
                                        <video
                                            v-if="editable_publication.intro_video_url"
                                            width="100%"
                                            height="auto"
                                            controls
                                        >
                                            <source :src="editable_publication.intro_video_url" type="video/mp4">
                                            Your browser does not support the video tag.
                                        </video>
                                        <div v-else class="p-4"> None Selected</div>
                                    </div>
                                </div>

                            </div>
                        </div>
                    </div>

                    <div class="row mt-4">
                        <template v-if="!editable_publication.intro_video_url">
                            <div class="col col-3 offset-9" v-if="!is_trunk">
                                <b-btn
                                    variant="success"
                                    class="mr-3 float-right"
                                    @click="addIntroVideo"
                                    :disabled="publication.locked"
                                >
                                    Add Intro Video
                                </b-btn>
                            </div>
                        </template>
                        <template v-else>
                            <div class="col col-2 offset-8">
                                <b-btn
                                    variant="primary"
                                    class="mr-3 float-right"
                                    @click="updateIntroVideo"
                                    :disabled="publication.locked"
                                >
                                    Update Intro Video
                                </b-btn>
                            </div>
                            <div class="col col-2">
                                <b-btn
                                    variant="danger"
                                    class="mr-3 float-right"
                                    @click="removeIntroVideo"
                                    :disabled="publication.locked"
                                >
                                    Remove
                                </b-btn>
                            </div>
                        </template>
                    </div>
                </div>

                <div v-if="!is_trunk" class="col-lg-6 pt-3 mb-3">
                    <div class="col-lg-12 pt-2">
                        <b-form-checkbox
                            v-model="editable_publication.allow_national_standards"
                            switch
                            :disabled="publication.locked"
                        >
                            Allow national standards
                        </b-form-checkbox>
                    </div>
                    <div class="col-lg-12 pt-2">
                        <b-form-checkbox
                            v-model="editable_publication.demo_available"
                            switch
                            :disabled="publication.locked"
                        >
                            Available as demo
                        </b-form-checkbox>
                    </div>
                    <div class="col-lg-12 pt-2">
                        <b-form-checkbox
                            v-model="editable_publication.visible_to_trial"
                            switch
                            :disabled="publication.locked"
                        >
                            Visible to trial
                        </b-form-checkbox>
                    </div>

                </div>

                <div v-if="!is_trunk" class="col-lg-6 pt-3 mb-3">
                    <div class="col-lg-12 pt-2">
                        <b-form-checkbox
                            v-model="editable_publication.visible_on_store"
                            switch
                            :disabled="publication.locked"
                        >
                            Visible in store
                        </b-form-checkbox>
                    </div>
                    <div class="col-lg-12 pt-2">
                        <b-form-checkbox
                            v-model="editable_publication.free_to_all"
                            switch
                            :disabled="publication.locked"
                        >
                            Free to all
                        </b-form-checkbox>
                    </div>
                </div>

                <div v-if="!is_trunk" class="col-lg-12 pt-2 mt-3 mb-3">
                    <b-form-checkbox
                        v-model="editable_publication.teacher_only"
                        switch
                        :disabled="publication.locked"
                    >
                        Teachers Only
                    </b-form-checkbox>
                    <div class="ml-3"
                         v-if="editable_publication.teacher_only == true || editable_publication.teacher_only == 'true'"
                    >
                        Access Linked Publications
                        <div
                            class="alert alert-primary mb-1"
                            style="width: 40%"
                            v-for="(aLink, index) in editable_publication.access_linked_publications"
                        >
                            {{ aLink.sku }}
                            <b-btn
                                variant="danger"
                                class="ml-3"
                                @click="remove_access_link(index, aLink)"
                                :disabled="publication.locked"
                            >
                                <font-awesome-icon :icon="['fad', 'trash']" size="sm"/>
                            </b-btn>
                        </div>

                        <div>
                            <b-form-select
                                ref="access_links"
                                v-model="access_link_to_add"
                                @change="add_access_link()"
                                style="width: 40%"
                                :disabled="publication.locked"
                            >
                                <b-form-select-option value="0" selected>
                                    Select Pub To Grant Linked Access
                                </b-form-select-option>
                                <b-form-select-option
                                    v-for="(pub_option, index) in publications"
                                    :key="index"
                                    :value="pub_option"
                                >
                                    {{ pub_option.sku }}
                                </b-form-select-option>
                            </b-form-select>
                        </div>
                    </div>
                </div>

                <div v-if="!is_trunk" class="col-lg-12">
                    <b-form-group label="Disclaimer (3.0)" label-for="disclaimer">
                        <b-input-group>
                            <b-form-textarea
                                ref="disclaimer"
                                type="text"
                                v-model="editable_publication.disclaimer"
                                :disabled="publication.locked"
                            />
                        </b-input-group>
                        <div class="col-lg-12 pt-2 pb-2 pl-0">
                            <b-form-checkbox
                                v-model="editable_publication.include_disclaimer"
                                switch
                                :disabled="publication.locked"
                            >
                                Include Disclaimer (3.0)
                            </b-form-checkbox>
                        </div>
                        <b-form-invalid-feedback
                            :class="{'d-block': validating && !editable_publication.disclaimer && editable_publication.include_disclaimer == true}"
                        >
                            Disclaimer text is required when marked as included.
                        </b-form-invalid-feedback>
                    </b-form-group>
                </div>

                <div class="col-lg-6 mb-2" v-if="!is_trunk">
                    <div class="h6">Cover Image (.jpg, .png)</div>
                    <div v-if="uploading_cover_image">
                        <b-spinner small label="Loading..."></b-spinner>
                    </div>
                    <div v-else>
                        <div v-if="editable_publication.cover_url">
                            <div class="row">
                                <div class="col-6">
                                    <img
                                        :src="'//s3-us-west-2.amazonaws.com/static.studiesweekly.com/' + editable_publication.cover_url"
                                        class="img-fluid img-thumbnail"
                                    >
                                </div>
                                <div class="d-flex align-items-center">
                                    <b-btn
                                        variant="danger"
                                        @click.prevent="removeCoverImage(false)"
                                        :disabled="publication.locked"
                                    >
                                        <font-awesome-icon :icon="['fad', 'trash']" size="lg"/>
                                    </b-btn>
                                </div>
                            </div>
                        </div>
                        <div v-else>
                            <b-form-file
                                v-model="cover_image"
                                accept="image/png, image/jpeg"
                                @change="addCoverImage($event, false)"
                                @drop.prevent="addCoverImage($event, false)"  
                                @dragover.prevent                
                                :disabled="publication.locked"
                            />
                        </div>
                    </div>
                </div>

                <div class="col-lg-6 mb-2" v-if="!is_trunk">
                    <div class="h6">Sample Cover Image (.jpg, .png)</div>
                    <div v-if="uploading_sample_cover_image">
                        <b-spinner small label="Loading..."></b-spinner>
                    </div>
                    <div v-else>
                        <div v-if="editable_publication.sample_cover_url">
                            <div class="row">
                                <div class="col-6">
                                    <img
                                        :src="'//s3-us-west-2.amazonaws.com/static.studiesweekly.com/' + editable_publication.sample_cover_url"
                                        class="img-fluid img-thumbnail"
                                    >
                                </div>
                                <div class="d-flex align-items-center">
                                    <b-btn
                                        variant="danger"
                                        @click.prevent="removeCoverImage(true)"
                                        :disabled="publication.locked"
                                    >
                                        <font-awesome-icon :icon="['fad', 'trash']" size="lg"/>
                                    </b-btn>
                                </div>
                            </div>
                        </div>
                        <div v-else>
                            <b-form-file
                                v-model="sample_cover_image"
                                accept="image/png, image/jpeg"
                                @change="addCoverImage($event, true)"
                                @drop.prevent="addCoverImage($event, true)"  
                                @dragover.prevent
                                :disabled="publication.locked"
                            />
                        </div>
                    </div>
                </div>
                <div v-if="publication.hasOwnProperty('panels')" class='col-lg-12 mb-2'>
                    <b-list-group>
                        <panel-section
                            :panels="publication.panels"
                            :publication="publication"
                            :disabled="publication.locked"
                        />
                    </b-list-group>
                </div>
            </div>

            <template #modal-footer>
                <b-button variant="light" @click="cancel">
                    Cancel
                </b-button>
                <b-button
                    :variant="editable_publication.id ? 'primary' : 'success'"
                    :disabled="checking_sku || checking_sample_sku || !can_edit_publication || publication.locked"
                    @click="savePublication"
                >
                    <span v-if="editable_publication.id">
                        <font-awesome-icon :icon="['fad', 'check-square']" fixed-width/> Save Changes
                    </span>
                    <span v-else>
                        <font-awesome-icon :icon="['fad', 'plus-square']" fixed-width/> Create
                        <span v-if="is_trunk">Trunk</span>
                        <span v-else>Publication</span>
                    </span>
                </b-button>
            </template>
        </b-modal>

        <add-related-media-modal
            v-model="show_add_related_media_modal"
            :type="'video'"
            @link="linkPublicationIntro"
            @hide="show_add_related_media_modal = false"
        />
    </div>
</template>

<script>
import {mapState} from 'vuex'

import formats from '@/common/formats'
import languages from '@/common/languages'

import school_years from '@/common/school_years'
import standards_years from '@/common/standards_years'
import subjects from '@/common/subjects'
import availabilities from '@/common/availabilities'
import categories from '@/common/publication_categories'
import badge_types from '@/common/badge_types'

import LanguageMultiselect from '@/components/LanguageMultiselect'
import PanelSection from '@/components/panels/PanelSection'

import AddRelatedMediaModal from '@/components/modals/article/related_media/AddRelatedMediaModal'
import TipTap from '@/components/tiptap/TipTap'

const RETIRE_PUBLICATION_MESSAGE = `
    <p>Saving this publication as Retired will also update these settings:</p>
    <ul class="text-left">
        <li>Visible to trial: <strong>No</strong></li>
        <li>Visible in store: <strong>No</strong></li>
        <li>Available as demo: <strong>No</strong></li>
        <li>Badge: <strong>Discontinued</strong></li>
    </ul>
`

export default {
    data: () => ({
        formats: formats,
        languages: languages,
        school_years: school_years,
        standards_years: standards_years,
        subjects: subjects,
        availabilities: availabilities,
        categories: categories,
        badge_types: badge_types,

        editable_publication: {},
        editable_publication_cache: {},

        title_editable: false,
        sample_sku_editable: false,

        checking_sku: false,
        checking_sample_sku: false,

        sku_available: null,
        sample_sku_available: null,

        suggested_title: null,

        cover_image: null,
        sample_cover_image: null,
        uploading_cover_image: false,
        uploading_sample_cover_image: false,

        show: true,

        states_visible: false,
        default_grade_to_add: '0',
        show_default_grade_select: false,

        legacy_id_options: [],
        access_link_to_add: 0,

        show_add_related_media_modal: false,
        updating_intro_video: false,

        pub_intro_video_url: null,

        adding_state: false,
        state_to_add: '',
        grade_to_add: '',
        standards_source_to_add: '',
    }),
    props: [
        'value',
        'publication',
        'states',
        'grades',
        'from_trunks',
        'readonly',
    ],
    components: {
        LanguageMultiselect,
        PanelSection,
        AddRelatedMediaModal,
        TipTap
    },
    computed: {
        ...mapState({
            validating: state => state.utilities.validating,
            publications: state => state.publications.publications,
            user: state => state.user.user
        }),
        can_set_portal_sku() {
            return this.user && this.user.permissions && _.includes(this.user.permissions, 'set_portal_sku')
        },
        linkablePublications() {
            return this.publications.filter(publication => {
                if (publication.id != this.publication.id) return true
            })
        },
        modal_id() {
            let modal_id = 'publication-modal'
            return modal_id
        },
        retiring_publication() {
            return !!this.editable_publication.id
                && this.editable_publication.category === 'retired'
                && this.editable_publication_cache.category !== 'retired'
        },
        publication_modified() {
            let publication_modified = false
            if (!_.isEqual(this.editable_publication, this.editable_publication_cache)) {
                publication_modified = true
            }
            return publication_modified
        },
        language: {
            get() {
                return {
                    language: this.editable_publication.language
                }
            },
            set(language) {
                this.$set(this.editable_publication, 'language', language.language)
            }
        },
        is_trunk() {
            return this.editable_publication.is_trunk === true || this.editable_publication.is_trunk === 'true'
        },
        can_edit_publication() {
            return this.user && this.user.permissions && _.includes(this.user.permissions, 'update_publication');
        },
        can_link_publication() {
            return this.user && this.user.permissions && _.includes(this.user.permissions, 'link_publication');
        },
        is_duplicate_state_grade() {
            const grades = this.editable_publication.state_grades;
            if (!Array.isArray(grades)) {
                return false;
            }
            return this.editable_publication.state_grades.some(sg => 
                sg.state === this.state_to_add && sg.grade === this.grade_to_add
            );
        }
    },
    watch: {
        value: {
            handler(value) {
                if (value == true) {
                    this.$store.commit('setValidating', false)

                    if (this.publication && this.publication.id) {
                        this.editable_publication = _.cloneDeep(this.publication)
                        this.editable_publication_cache = _.cloneDeep(this.publication)
                    } else {
                        this.editable_publication = {
                            state_grades: [{}],
                            access_linked_publications: [],
                        }
                        this.editable_publication_cache = {
                            state_grades: [{}],
                            access_linked_publications: []
                        }
                    }
                    if (!this.publications.length) {
                        this.$store.dispatch('getPublications')
                    }

                    if (this.from_trunks) {
                        this.editable_publication.is_trunk = true;
                    }
                }
            }
        },
        editable_publication: {
            handler(publication) {
                let stateGradeValues = null

                if (publication.state_grades && publication.state_grades.length && publication.state_grades.length == 1) {
                    _.forEach(publication.state_grades, sg => {
                        if (sg.state && sg.grade) {
                            stateGradeValues = true
                        } else {
                            stateGradeValues = false
                        }
                    })
                } else {
                    stateGradeValues = null
                }

                if (publication.subject && stateGradeValues && publication.language) {
                    this.suggestTitle()
                }
                if (!publication.badge_type || publication.badge_type === 'discontinued') {
                    publication.badge_end_date = null
                }
            },
            deep: true,
            immediate: true
        }
    },
    methods: {
        isIntroVideoTitleMissing() {
            if (!this.editable_publication || this.is_trunk) return false

            const introVideoUrl = this.editable_publication.intro_video_url
            if (!introVideoUrl) return false

            const introVideoTitle = this.editable_publication.intro_video_title
            return !introVideoTitle || !String(introVideoTitle).trim()
        },
        addCoverImage(event, is_sample) {
            let file = null
            let form_data = new FormData()

            // Case 1: drag & drop (DropEvent)
            if (event && event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files.length) {
                file = event.dataTransfer.files[0];
            }
            // Case 2: browse (ChangeEvent from input[type="file"])
            else if (event.target && event.target.files && event.target.files.length) {
                file = event.target.files[0];
            }

            form_data.append('cover_image', file)
            form_data.append('is_sample', is_sample)

            if (is_sample) this.uploading_sample_cover_image = true
            else this.uploading_cover_image = true

            this.axios.post('/api/publications/add_cover_image/' + this.editable_publication.id, form_data)
                .then(response => {
                    this.editable_publication.cover_url = response.data.cover_url;
                    this.editable_publication.sample_cover_url = response.data.sample_cover_url;
                    if (is_sample) {
                        this.uploading_sample_cover_image = false
                        this.sample_cover_image = null
                    } else {
                        this.uploading_cover_image = false
                        this.cover_image = null
                    }
                })
                .catch(error => {
                    if (error) {
                        if (is_sample) {
                            this.uploading_sample_cover_image = false
                            this.sample_cover_image = null
                        } else {
                            this.uploading_cover_image = false
                            this.cover_image = null
                        }
                    }
                })
        },
        removeCoverImage(is_sample) {
            let message = null

            message = 'Are you sure you want to remove the ' + (is_sample ? 'sample' : '') + ' cover image for <strong>' + this.editable_publication.title + '</strong>?'

            this.$swal({
                html: message,
                showCancelButton: true,
                confirmButtonText: 'Remove Cover Image',
                cancelButtonText: 'Cancel',
                reverseButtons: true
            }).then((result) => {
                if (result.value) {
                    this.axios.post('/api/publications/remove_cover_image/' + this.editable_publication.id, {
                        is_sample: is_sample
                    })
                        .then(() => {
                            if (is_sample) this.editable_publication.sample_cover_url = null
                            else this.editable_publication.cover_url = null
                        })
                }
            })
        },
        add_access_link() {
            this.editable_publication.access_linked_publications.push({
                linked_publication_id: this.access_link_to_add.id,
                sku: this.access_link_to_add.sku
            });
            this.access_link_to_add = 0;
        },
        remove_access_link(aLinkIndex, aLink) {
            this.editable_publication.access_linked_publications.splice(aLinkIndex, 1);
        },
        async savePublication(event) {
            event.preventDefault()

            this.$store.commit('setValidating', true)

            if (!this.validated()) {
                this.states_visible = true;
                return false
            }
            if (this.retiring_publication) {
                const result = await this.$swal({
                    title: 'Retire publication?',
                    icon: 'info',
                    html: RETIRE_PUBLICATION_MESSAGE,
                    showCancelButton: true,
                    confirmButtonText: 'Retire Publication',
                    cancelButtonText: 'Keep Editing',
                    reverseButtons: true
                })
                if (!result.value) { 
                    return false
                }
            }
            if (this.editable_publication.is_trunk) {
                let currentDate = new Date();
                let currentYear = currentDate.getFullYear();
                this.editable_publication.school_year = currentYear;
            }
            if (this.publication_modified) {
                if (this.editable_publication.id) {
                    this.axios.post('/api/publications/update/' + this.editable_publication.id,
                        this.editable_publication
                    ).then(() => {
                        this.$emit("update")

                        this.hide()
                    })
                } else {
                    this.axios.post('/api/publications/create', this.editable_publication)
                        .then(response => {
                            this.$emit("create", this.editable_publication, response)

                            this.hide()
                        })
                }
            } else {
                this.hide()
            }
        },
        validated() {
            let validated = true
            if (!this.editable_publication.subject) {
                validated = false
                this.$refs.subject.focus()
                return false
            } else if (!this.editable_publication.school_year && !this.editable_publication.is_trunk) {
                validated = false
                this.$refs.school_year.focus()
                return false
            } else if (!this.editable_publication.format) {
                validated = false
                this.$refs.format.focus()
                return false
            } else if (!this.editable_publication.language) {
                validated = false
                this.$refs.language.focus()
                return false
            } else if (!this.editable_publication.title) {
                validated = false
                this.$refs.title.focus()
                return false
            } else if (this.isIntroVideoTitleMissing()) {
                validated = false
                this.$refs.intro_video_title.focus()
                return false
            } else if (!this.editable_publication.disclaimer && this.editable_publication.include_disclaimer == true && !this.editable_publication.is_trunk) {
                validated = false
                this.$refs.disclaimer.focus()
                return false
            } else if (!this.editable_publication.sku || this.sku_available == false) {
                validated = false
                this.$refs.sku.focus()
                return false
            } else if (this.editable_publication.sample_sku && this.sample_sku_available == false && !this.editable_publication.is_trunk) {
                validated = false
                this.$refs.sample_sku.focus()
                return false
            } else if (this.checking_sku || this.checking_sample_sku) {
                validated = false
            } else if (this.editable_publication.badge_type && this.editable_publication.badge_type !== 'discontinued' && !this.editable_publication.is_trunk && !this.retiring_publication) {
                if (!this.editable_publication.badge_end_date) {
                    this.$refs.badge_end_date.focus()
                    validated = false
                }
            }

            if (!this.editable_publication.is_trunk) {
                _.forEach(this.editable_publication.state_grades, (sg) => {
                    if (!sg.state) {
                        validated = false
                        this.states_visible = true;
                        return false
                    } else if (!sg.grade) {
                        validated = false
                        this.states_visible = true;
                        return false
                    }
                })
            }
            return validated
        },
        addStateGrade() {
            this.editable_publication.state_grades.push({
                state: '',
                grade: '',
            })
        },
        showDefaultGradeSelect() {
            this.show_default_grade_select = !this.show_default_grade_select;
        },
        addAllStates() {
            if (this.editable_publication.state_grades[0].state == undefined) {
                this.editable_publication.state_grades.shift();
            }

            let no_add = [
                'NA', 'FM', 'GU', 'MH', 'MP', 'PW', 'VI'
            ];
            _.forEach(this.states, (state) => {
                let match = false;
                _.forEach(this.editable_publication.state_grades, (assigned_state) => {
                    if (state.abbreviation == assigned_state.state) {
                        match = true;
                    }
                })
                if (!match && !no_add.includes(state.abbreviation)) {
                    this.editable_publication.state_grades.push({
                        state: state.abbreviation,
                        grade: this.default_grade_to_add,
                        standards_source: 'state',
                    })
                }
            })
        },
        removeStateGrade(index) {
            if (this.editable_publication.state_grades) {
                this.editable_publication.state_grades.splice(index, 1)
            }
        },
        formatSku(sku) {
            if (sku && sku.length) {
                this.checking_sku = true
                sku = sku.toUpperCase()
                this.$set(this.editable_publication, 'sku', sku)
                this.checkSkuAvailability(sku)

                if (!this.editable_publication_cache.sample_sku) {
                    let sample_sku = sku
                    if (!sku.endsWith('-SAMPLE')) {
                        sample_sku = sample_sku + '-SAMPLE'
                    }
                    sample_sku = sample_sku.toUpperCase()
                    this.$set(this.editable_publication, 'sample_sku', sample_sku)
                    this.checkSampleSkuAvailability(sample_sku)
                }
            } else {
                this.checking_sku = false
                this.sku_available = null
                this.sample_sku_available = null
                this.$set(this.editable_publication, 'sample_sku', null)
            }
        },
        formatSampleSku(sku) {
            if (sku && sku.length) {
                this.checking_sample_sku = true
                sku = sku.toUpperCase()
                this.$set(this.editable_publication, 'sample_sku', sku)
                this.setSampleSku(sku)
            } else {
                this.checking_sample_sku = false
                this.sample_sku_available = null
            }
        },
        setSampleSku: _.debounce(function (sku) {
            if (!sku.endsWith('-SAMPLE')) {
                sku = sku + '-SAMPLE'
            }
            this.$set(this.editable_publication, 'sample_sku', sku)
            this.checkSampleSkuAvailability(sku)
        }, 1000),
        checkSkuAvailability: _.debounce(function (sku) {
            if (_.isEqual(sku, this.editable_publication_cache.sku)) {
                this.sku_available = null
                this.checking_sku = false
                return false
            }

            this.checking_sku = true

            this.axios.get("/api/publications/skuAvailable/" + sku)
                .then(response => {
                    this.checking_sku = false
                    if (response) {
                        this.sku_available = response.data
                    }
                })
                .catch(error => {
                    this.checking_sku = false
                    if (error) {
                        console.log(error)
                    }
                })
        }, 1000),
        checkSampleSkuAvailability: _.debounce(function (sku) {
            if (_.isEqual(sku, this.editable_publication_cache.sample_sku)) {
                this.sample_sku_available = null
                this.checking_sample_sku = false
                return false
            }

            this.axios.get("/api/publications/skuAvailable/" + sku)
                .then(response => {
                    if (response) {
                        this.checking_sample_sku = false
                        this.sample_sku_available = response.data
                    }
                })
                .catch(error => {
                    this.checking_sample_sku = false
                    if (error) {
                        console.log(error)
                    }
                })
        }, 1000),
        suggestTitle() {
            let publication = this.editable_publication
            let stateGrades = this.editable_publication.state_grades

            if (stateGrades.length > 1) return

            if (publication.subject && publication.language && stateGrades[0].state !== '' && stateGrades[0].grade !== '') {
                let stateName, gradeName, subjectName

                _.each(this.states, state => {
                    if (stateGrades[0].state == state.abbreviation) {
                        stateName = state.name
                    }
                })

                _.each(this.grades, grade => {
                    if (stateGrades[0].grade == grade.grade) {
                        gradeName = grade.name
                    }
                })

                _.each(this.subjects, subject => {
                    if (publication.subject == subject.subject) {
                        subjectName = subject.name
                    }
                })

                let languageName = ''
                let suggested_title = ''

                if (publication.language == 'es' || publication.language == 'sp') {
                    languageName = 'Spanish'
                    suggested_title = stateName + ' ' + gradeName + ' ' + languageName + ' ' + subjectName
                } else {
                    suggested_title = stateName + ' ' + gradeName + ' ' + subjectName
                }

                if (suggested_title && !this.editable_publication.title) {
                    this.$set(this.editable_publication, 'title', '')
                    this.$set(this.editable_publication, 'title', suggested_title)
                } else if (suggested_title && this.editable_publication.title && this.editable_publication.title !== suggested_title) {
                    this.suggested_title = suggested_title
                } else if (suggested_title && this.editable_publication.title && this.editable_publication.title == suggested_title) {
                    this.suggested_title = null
                }
            }
        },
        useSuggestedTitle(suggested_title) {
            if (this.editable_publication.title) {
                this.$set(this.editable_publication, 'title', suggested_title)
            } else {
                this.$set(this.editable_publication, 'title', '')
                this.$set(this.editable_publication, 'title', suggested_title)
            }
        },
        addLanguage(new_language) {
            this.language = new_language
        },
        removeLanguage() {
            this.language = {}
        },
        addIntroVideo() {
            this.show_add_related_media_modal = true;
        },
        updateIntroVideo() {
            this.show_add_related_media_modal = true;
        },
        linkPublicationIntro(item) {
            this.editable_publication.intro_video_url = null;
            this.editable_publication.intro_video_splash_url = null;
            this.editable_publication.intro_video_title = null;
            this.updating_intro_video = true;

            this.$nextTick(() => {
                this.editable_publication.intro_video_url = item.url;
                this.editable_publication.intro_video_splash_url = item.splash_url;
                if (!this.editable_publication.intro_video_title) {
                    this.editable_publication.intro_video_title = item.title;
                }
                this.updating_intro_video = false;
                this.show_add_related_media_modal = false;
            });
        },
        removeIntroVideo() {
            this.editable_publication.intro_video_url = null;
            this.editable_publication.intro_video_splash_url = null;
            this.editable_publication.intro_video_title = null;
        },
        cancel(event) {
            event.preventDefault()

            if (this.publication_modified) {
                this.$swal({
                    html: 'Changes have been made to this publication. If you proceed, all changes will be lost.',
                    showCancelButton: true,
                    confirmButtonText: 'Discard Changes',
                    cancelButtonText: 'Keep Editing',
                    reverseButtons: true
                }).then((result) => {
                    if (result.value) {
                        this.hide()
                    }
                })
            } else {
                this.hide()
            }
        },
        hide() {
            this.$emit('hide')
        },
        hidden() {
            this.editable_publication = {}
            this.editable_publication_cache = {}

            this.title_editable = false
            this.sample_sku_editable = false

            this.suggested_title = null

            this.sku_available = null
            this.sample_sku_available = null

            this.checking_sku = false
            this.checking_sample_sku = false

            this.$emit('hide')
        },

        toggleStatesVisible() {
            this.states_visible = !this.states_visible;
        },
        checkStandardTypeAvailability (state, grade, standard_source) {
            let sources = [];
            _.each(this.editable_publication.state_grades, state_grade => {
                if(!sources[state_grade.state]){
                    sources[state_grade.state] = {
                        grade: state_grade.grade, 
                        source: state_grade.standards_source
                    };
                }
            })
            if(sources[state].source && sources[state].source != standard_source && sources[state].grade != grade){
                return true;
            }
            else{
                return false;
            }
        },
        updateOtherGrades($event, state, grade){
            this.editable_publication.state_grades.forEach((state_grade, index) => {
                if (state_grade.state === state) {
                    this.$set(this.editable_publication.state_grades, index, { 
                        ...state_grade, 
                        standards_source: $event 
                    });
                }
            });
        },
        reorderStateGrades(){
            const sortedGrades = _.orderBy(
                this.editable_publication.state_grades, 
                ['state', 'grade'], 
                ['asc', 'asc']
            );
            // Force Vue to recognize the change
            this.$set(this.editable_publication, 'state_grades', sortedGrades);
        },
        addNewStateGrade(){
            let new_state = {
                state: this.state_to_add,
                grade: this.grade_to_add,
                standards_source: this.standards_source_to_add
            };
            //add to list of states
            this.editable_publication.state_grades.push(new_state);
            //reset form
            this.state_to_add = "";
            this.grade_to_add = "";
            this.standards_source_to_add = "";

            this.reorderStateGrades();
        },
    },
    mounted() {
        this.axios.post("/api/publications/get_legacy_id_options")
            .then(response => {
                this.legacy_id_options = response.data.products;
            })
            .catch(error => {
                this.checking_sample_sku = false
                if (error) {
                    console.log(error)
                }
            })
    }
}
</script>
